import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ConfirmationModalComponent } from '../components/confirmation-modal/confirmation-modal.component';
import { AppConstants } from '../utilities/constant';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class ConfirmationService {

  constructor(private modalController: ModalController,
        public translate: TranslateService) {}

  // Accept optional parameters with default values
  async presentConfirmationModal(
    confirmationTitle: string = this.translate.instant(AppConstants.ARE_YOU_SURE_TO_DELETE), // Default title
    actionText: string = this.translate.instant(AppConstants.DELETE) , // Default action button text
    showCancel: boolean = false,
    cancelText: string = this.translate.instant(AppConstants.CANCEL), // Default cancel button text
    confirmationSubtitle?: string, // Optional subtitle
    confirmationContent?: {
      confirmationMessage?: string;
      noticeTitle?: string;
      noticeMessage?: string;
      modalCssClass?: string;
      showActionButton?: boolean;
      useFullMessage?: boolean;
    }
  ): Promise<boolean> {
    const cssClass = confirmationContent?.modalCssClass
      ? ['confirmation-modal', confirmationContent.modalCssClass]
      : 'confirmation-modal';

    const modal = await this.modalController.create({
      component: ConfirmationModalComponent,
      cssClass,
      backdropDismiss: false,
      showBackdrop: false,
      componentProps: {
        confirmationTitle,
        confirmationSubtitle,
        confirmationMessage: confirmationContent?.confirmationMessage,
        noticeTitle: confirmationContent?.noticeTitle,
        noticeMessage: confirmationContent?.noticeMessage,
        actionText,
        showActionButton: confirmationContent?.showActionButton ?? true,
        useFullMessage: confirmationContent?.useFullMessage ?? false,
        showCancel,
        cancelText,
      },
    });

    await modal.present();
    const { data } = await modal.onWillDismiss();
    return !!data?.isActionConfirmed;
  }

  /**
   * Presents an "unsaved changes" modal offering Save / Discard / Cancel.
   * Returns the user's choice so callers can centralize navigation guards.
   */
  async presentUnsavedChangesModal(
    title: string = this.translate.instant('Unsaved_Changes_Title'),
    message: string = this.translate.instant('Unsaved_Changes_Message'),
    options: {
      actionText?: string;
      cancelText?: string;
      discardText?: string;
      showCancel?: boolean;
      modalCssClass?: string;
      useFullMessage?: boolean;
    } = {}
  ): Promise<'save' | 'discard' | 'cancel'> {
    const modal = await this.modalController.create({
      component: ConfirmationModalComponent,
      cssClass: ['confirmation-modal', 'unsaved-changes-modal', ...(options.modalCssClass ? [options.modalCssClass] : [])],
      backdropDismiss: false,
      showBackdrop: false,
      componentProps: {
        confirmationTitle: title,
        confirmationMessage: message,
        actionText: this.translate.instant(options.actionText || 'Save'),
        showDiscardButton: true,
        discardText: this.translate.instant(options.discardText || 'Discard'),
        showCancel: options.showCancel ?? true,
        cancelText: this.translate.instant(options.cancelText || 'Cancel'),
        useFullMessage: options.useFullMessage ?? false,
      },
    });

    await modal.present();
    const { data } = await modal.onWillDismiss();

    if (data?.isActionConfirmed) {
      return 'save';
    }
    if (data?.isDiscarded) {
      return 'discard';
    }
    return 'cancel';
  }
}
